/**
 * Faceted availability engine — the single source of truth for "who can teach
 * what, where, when" across the inquiry form and prospect booking.
 *
 * Given a normalized teacher set and a partial Selection (any subset of level /
 * instrument / location / day / time / teacher), it returns the valid OPTIONS for
 * every dimension — each computed by matching on all the OTHER active selections.
 * That's what makes the dropdowns interdependent, order-independent, and free of
 * dead-ends: an option only appears if picking it yields ≥1 real teacher.
 *
 * Data model (from the real data): location coverage is teacher-level
 * (teach_locations, incl. 'Virtual'); day/time is window-level (their availability
 * windows). Instrument is teachers.programs (program_scope is unused/null).
 */

export interface AvailWindow {
  day: string; // 'Monday' … 'Sunday'
  startMin: number; // minutes from midnight
  endMin: number;
}

export interface MatchTeacher {
  id: string;
  name: string;
  level: string; // 'Standard' | 'Master' | 'Director'
  programs: string[]; // instruments taught
  locations: string[]; // teach_locations (canonical), may include 'Virtual'
  windows: AvailWindow[];
}

export type Dimension = 'level' | 'instrument' | 'location' | 'day' | 'time' | 'teacher';

export interface Selection {
  level?: string | null;
  instrument?: string | null;
  location?: string | null;
  day?: string | null;
  time?: string | null; // a TIME_BUCKET label
  teacherId?: string | null;
}

export interface Option {
  value: string;
  count: number; // how many teachers this option keeps available
}

export interface Facets {
  levels: Option[];
  instruments: Option[];
  locations: Option[];
  days: Option[];
  times: Option[];
  teachers: (MatchTeacher & { count: number })[];
}

export const DAYS_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const TIME_BUCKETS: { label: string; start: number; end: number }[] = [
  { label: 'Morning (8am–12pm)', start: 8 * 60, end: 12 * 60 },
  { label: 'Afternoon (12pm–4pm)', start: 12 * 60, end: 16 * 60 },
  { label: 'Evening (4pm–8pm)', start: 16 * 60, end: 20 * 60 },
];

/** "09:00" | "09:00:00" | "9 AM" → minutes from midnight (null if unparseable). */
export function toMinutes(t: string | null | undefined): number | null {
  if (!t) return null;
  const hm = /^(\d{1,2}):(\d{2})/.exec(t);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2]);
  const ap = /^(\d{1,2})\s*(AM|PM)$/i.exec(t.trim());
  if (ap) {
    const h = Number(ap[1]) % 12;
    return (h + (/pm/i.test(ap[2]) ? 12 : 0)) * 60;
  }
  return null;
}

function bucket(label: string) {
  return TIME_BUCKETS.find((b) => b.label === label) ?? null;
}

/** Does a window overlap the given time-bucket label? */
function windowInTime(w: AvailWindow, timeLabel: string): boolean {
  const b = bucket(timeLabel);
  if (!b) return true; // unknown bucket → don't over-filter
  return w.startMin < b.end && w.endMin > b.start;
}

/**
 * Does a teacher satisfy the selection? `ignore` lets a facet exclude its own
 * dimension so its options aren't self-limiting (that's the reversibility trick).
 */
export function matchesSelection(t: MatchTeacher, sel: Selection, ignore?: Dimension): boolean {
  if (ignore !== 'teacher' && sel.teacherId && t.id !== sel.teacherId) return false;
  if (ignore !== 'level' && sel.level && t.level !== sel.level) return false;
  if (ignore !== 'instrument' && sel.instrument && !t.programs.includes(sel.instrument))
    return false;
  if (ignore !== 'location' && sel.location && !t.locations.includes(sel.location)) return false;

  const day = ignore !== 'day' ? sel.day : null;
  const time = ignore !== 'time' ? sel.time : null;
  if (day || time) {
    const ok = t.windows.some((w) => (!day || w.day === day) && (!time || windowInTime(w, time)));
    if (!ok) return false;
  }
  return true;
}

/** Teachers who satisfy the full selection. */
export function matchTeachers(teachers: MatchTeacher[], sel: Selection): MatchTeacher[] {
  return teachers.filter((t) => matchesSelection(t, sel));
}

function tally(values: string[]): Option[] {
  const m = new Map<string, number>();
  for (const v of values) m.set(v, (m.get(v) ?? 0) + 1);
  return [...m.entries()].map(([value, count]) => ({ value, count }));
}

/**
 * Compute the valid options for every dimension given the current selection.
 * Each dimension is computed against all OTHER selected dimensions, so options
 * never dead-end and the picker is reversible in any order.
 */
export function computeFacets(teachers: MatchTeacher[], sel: Selection): Facets {
  const poolFor = (dim: Dimension) => teachers.filter((t) => matchesSelection(t, sel, dim));

  // level: one row per teacher in the level-pool
  const levels = tally(poolFor('level').map((t) => t.level)).sort((a, b) =>
    a.value.localeCompare(b.value)
  );

  // instrument: union of programs across the instrument-pool
  const instruments = tally(poolFor('instrument').flatMap((t) => t.programs)).sort((a, b) =>
    a.value.localeCompare(b.value)
  );

  // location: union of teach_locations across the location-pool ('Virtual' sorted last)
  const locations = tally(poolFor('location').flatMap((t) => t.locations)).sort((a, b) => {
    if (a.value === 'Virtual') return 1;
    if (b.value === 'Virtual') return -1;
    return a.value.localeCompare(b.value);
  });

  // day: union of window days across the day-pool
  const dayPool = poolFor('day');
  const days = tally(dayPool.flatMap((t) => [...new Set(t.windows.map((w) => w.day))])).sort(
    (a, b) => DAYS_ORDER.indexOf(a.value) - DAYS_ORDER.indexOf(b.value)
  );

  // time: buckets any window in the time-pool overlaps (count = teachers with a hit)
  const timePool = poolFor('time');
  const times = TIME_BUCKETS.map((b) => ({
    value: b.label,
    count: timePool.filter((t) => t.windows.some((w) => windowInTime(w, b.label))).length,
  })).filter((o) => o.count > 0);

  // teacher: the fully-matched pool (ignoring the teacher dimension itself)
  const teacherPool = poolFor('teacher').map((t) => ({ ...t, count: 1 }));

  return { levels, instruments, locations, days, times, teachers: teacherPool };
}

// ─── Soonest-slot recommendation (Layer 3) ──────────────────────────────────────

export interface ExistingLesson {
  teacherId: string;
  date: string; // YYYY-MM-DD
  startMin: number;
  endMin: number;
}

export interface SlotRecommendation {
  teacherId: string;
  teacherName: string;
  level: string;
  date: string; // YYYY-MM-DD
  startMin: number;
  endMin: number;
  inWindow: boolean; // falls inside the family's stated day + time preference
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Add N days to a YYYY-MM-DD string, UTC-safe (no DST drift). */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().split('T')[0];
}
function weekdayOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  return DAY_NAMES[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
}
export function fmtMin(min: number): string {
  const h = Math.floor(min / 60);
  const mm = min % 60;
  const ap = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mm).padStart(2, '0')} ${ap}`;
}

/**
 * Find the soonest genuinely-bookable slots for an inquiry: real teacher windows
 * minus existing lessons, scanned forward from `fromDate`. Slots inside the
 * family's stated day/time preference rank first (never blocked — soft), then by
 * soonest. `teachers` should already be matched (instrument/location/level).
 */
export function findSoonestSlots(
  teachers: MatchTeacher[],
  lessons: ExistingLesson[],
  prefDays: string[],
  prefTimes: string[],
  fromDate: string,
  horizonDays: number,
  durationMin: number,
  limit = 5
): SlotRecommendation[] {
  const busy = new Map<string, { s: number; e: number }[]>();
  for (const l of lessons) {
    const k = `${l.teacherId}|${l.date}`;
    const arr = busy.get(k);
    if (arr) arr.push({ s: l.startMin, e: l.endMin });
    else busy.set(k, [{ s: l.startMin, e: l.endMin }]);
  }
  const prefDaySet = new Set(prefDays);
  const out: SlotRecommendation[] = [];
  for (let i = 0; i <= horizonDays; i++) {
    const date = addDays(fromDate, i);
    const dname = weekdayOf(date);
    const inDay = prefDaySet.size === 0 || prefDaySet.has(dname);
    for (const t of teachers) {
      const windows = t.windows.filter((w) => w.day === dname);
      if (!windows.length) continue;
      const taken = busy.get(`${t.id}|${date}`) ?? [];
      for (const w of windows) {
        for (let start = w.startMin; start + durationMin <= w.endMin; start += durationMin) {
          const end = start + durationMin;
          if (taken.some((b) => start < b.e && end > b.s)) continue;
          const inTime =
            prefTimes.length === 0 ||
            prefTimes.some((lbl) => {
              const b = TIME_BUCKETS.find((x) => x.label === lbl);
              return b ? start < b.end && end > b.start : true;
            });
          out.push({
            teacherId: t.id,
            teacherName: t.name,
            level: t.level,
            date,
            startMin: start,
            endMin: end,
            inWindow: inDay && inTime,
          });
        }
      }
    }
  }
  out.sort(
    (a, b) =>
      Number(b.inWindow) - Number(a.inWindow) ||
      a.date.localeCompare(b.date) ||
      a.startMin - b.startMin
  );
  return out.slice(0, limit);
}
